/**
 * CommandPalette — ⌘K / Ctrl+K quick switcher.
 *
 * Three jobs in one surface:
 *   1. Jump to any view / sub-tab (deep-links through the same callbacks
 *      the TopMenu uses, so navigation stays consistent).
 *   2. Jump straight to a *record* — type a contact, account, ticket,
 *      task, or agent name/id and land on it already open (via onJump →
 *      App focus state). With no query, recently-jumped records show first.
 *   3. Hand a free-text instruction straight to the Argo agent — the
 *      "Ask Argo: …" row routes to the Chat view and auto-sends the query.
 *
 * Open/close + the global key listener live in App.tsx; this component
 * is purely the overlay UI and keyboard navigation within the list.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Bot,
  Building2,
  Clock,
  Code2,
  CornerDownLeft,
  FolderKanban,
  Gauge,
  LayoutDashboard,
  ListChecks,
  MessageSquare,
  ScanFace,
  Search,
  Shield,
  Sparkles,
  Target,
  Ticket as TicketIcon,
  UserCircle2,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { matchesCommand, rankHit } from '../lib/commandFilter';
import { useFocusTrap } from '../lib/useFocusTrap';
import type { ViewKey } from './TopMenu';
import type { ContactsTab } from '../views/PeopleView';
import type { ProjectsTab } from '../views/ProjectsView';
import { subscribePeople } from '../lib/db/people';
import { subscribeAccounts } from '../lib/db/accounts';
import { subscribeTickets } from '../lib/db/tickets';
import { subscribeTasks } from '../lib/db/tasks';
import { subscribeAgents } from '../lib/db/agents';
import type {
  Account,
  DeployedAgent,
  Person,
  PersonCategory,
  ProjectTask,
  Ticket,
} from '../lib/db/types';

/**
 * A record the palette can deep-link to. App turns this into the right
 * view + focus state so the entity opens on arrival.
 */
export type JumpTarget =
  | { kind: 'person'; id: string; tab: ContactsTab }
  | { kind: 'account'; id: string }
  | { kind: 'ticket'; id: string }
  | { kind: 'task'; id: string }
  | { kind: 'agent'; id: string };

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: ViewKey) => void;
  onSelectContactsTab: (tab: ContactsTab) => void;
  onSelectProjectsTab: (tab: ProjectsTab) => void;
  onAskArgo: (text: string) => void;
  onJump: (target: JumpTarget) => void;
  canAdmin: boolean;
}

interface NavCommand {
  id: string;
  label: string;
  hint: string;
  group: 'Navigate' | 'Projects' | 'Contacts' | 'Admin';
  icon: LucideIcon;
  /** Extra search terms so e.g. "kanban" finds Workflows. */
  keywords?: string;
  run: () => void;
}

/** A matched record, ready to render as a "Jump to" row. */
interface EntityMatch {
  target: JumpTarget;
  title: string;
  sub: string;
  /** Lower = better (index of the query hit in the matched field). */
  rank: number;
}

/** A previously-jumped record, persisted so an empty palette is useful. */
interface RecentEntry {
  target: JumpTarget;
  title: string;
  sub: string;
}

const RECENTS_KEY = 'argo-os:cmdk-recents';
const RECENTS_MAX = 6;

function loadRecents(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: RecentEntry) {
  try {
    const deduped = loadRecents().filter(
      (r) => !(r.target.kind === entry.target.kind && r.target.id === entry.target.id),
    );
    const next = [entry, ...deduped].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable — recents are a nice-to-have, not load-bearing.
  }
}

/** Map a person's CRM category to the Contacts sub-tab that lists them. */
function personTab(category: PersonCategory): ContactsTab {
  if (category === 'prospect') return 'prospects';
  if (category === 'persona') return 'persona';
  return 'contacts';
}

/** Icon + accent for a jump target — derived from the target alone so
 *  live matches and persisted recents render identically. */
function visualForTarget(t: JumpTarget): { icon: LucideIcon; color: string } {
  switch (t.kind) {
    case 'person':
      if (t.tab === 'prospects') return { icon: Target, color: C.orange };
      if (t.tab === 'persona') return { icon: ScanFace, color: C.magentaSoft };
      return { icon: UserCircle2, color: C.magentaSoft };
    case 'account':
      return { icon: Building2, color: C.blueElectric };
    case 'ticket':
      return { icon: TicketIcon, color: C.green };
    case 'task':
      return { icon: ListChecks, color: C.orange };
    case 'agent':
      return { icon: Bot, color: C.magentaSoft };
  }
}

/** Render `text` with the first case-insensitive hit of `q` emphasised. */
function Highlight({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: 'transparent', color: '#fff', fontWeight: 600 }}>
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
}

/** Unified, keyboard-navigable row model (ask / entity / nav). */
type Row =
  | { type: 'ask'; text: string }
  | { type: 'entity'; match: EntityMatch }
  | { type: 'nav'; cmd: NavCommand };

export function CommandPalette({
  open,
  onClose,
  onNavigate,
  onSelectContactsTab,
  onSelectProjectsTab,
  onAskArgo,
  onJump,
  canAdmin,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const [people, setPeople] = useState<Person[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [recents, setRecents] = useState<RecentEntry[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(panelRef, open);

  // Reset query + selection each time the palette opens, and refresh the
  // recents list from storage so it reflects the latest jumps.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSel(0);
      setRecents(loadRecents());
      // Focus the input after the focus-trap settles.
      queueMicrotask(() => inputRef.current?.focus({ preventScroll: true }));
    }
  }, [open]);

  // Subscribe to the searchable collections only while the palette is open,
  // so the closed palette costs nothing.
  useEffect(() => {
    if (!open) return;
    let active = true;
    const unsubs = [
      subscribePeople((r) => active && setPeople(r)),
      subscribeAccounts((r) => active && setAccounts(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribeAgents((r) => active && setAgents(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, [open]);

  const commands = useMemo<NavCommand[]>(() => {
    const go = (view: ViewKey) => () => {
      onNavigate(view);
      onClose();
    };
    const goContacts = (tab: ContactsTab) => () => {
      onSelectContactsTab(tab);
      onNavigate('contacts');
      onClose();
    };
    const goProjects = (tab: ProjectsTab) => () => {
      onSelectProjectsTab(tab);
      onNavigate('projects');
      onClose();
    };
    const list: NavCommand[] = [
      { id: 'dashboard', label: 'Dashboard', hint: 'Where to spend your day', group: 'Navigate', icon: LayoutDashboard, run: go('dashboard') },
      { id: 'accounts', label: 'Accounts', hint: 'CRM list — POC, owner, stage', group: 'Contacts', icon: Building2, keywords: 'crm customers', run: goContacts('accounts') },
      { id: 'contacts', label: 'Contacts', hint: 'People at active accounts', group: 'Contacts', icon: UserCircle2, run: goContacts('contacts') },
      { id: 'prospects', label: 'Prospects', hint: 'Pipeline of individuals', group: 'Contacts', icon: Target, keywords: 'leads pipeline', run: goContacts('prospects') },
      { id: 'personas', label: 'Personas', hint: 'Buyer persona library', group: 'Contacts', icon: ScanFace, keywords: 'icp', run: goContacts('persona') },
      { id: 'clients', label: 'Clients', hint: 'Client directory', group: 'Projects', icon: Building2, keywords: 'accounts directory', run: goProjects('clients') },
      { id: 'projects', label: 'Projects', hint: 'Delivery projects — list / kanban', group: 'Projects', icon: FolderKanban, keywords: 'kanban board delivery', run: goProjects('projects') },
      { id: 'tasks', label: 'Tasks', hint: 'Project task board', group: 'Projects', icon: ListChecks, run: goProjects('tasks') },
      { id: 'agents', label: 'Agents', hint: 'Deployed Argo agents', group: 'Projects', icon: Bot, keywords: 'automation', run: goProjects('agents') },
      { id: 'development', label: 'Development', hint: 'Roadmap · north star · milestones', group: 'Projects', icon: Code2, keywords: 'roadmap milestones docs', run: goProjects('development') },
      { id: 'tickets', label: 'Tickets', hint: 'Claude autopilot queue', group: 'Navigate', icon: TicketIcon, keywords: 'support autopilot', run: go('tickets') },
      { id: 'tools', label: 'Tools', hint: 'Utilities and generators', group: 'Navigate', icon: Wrench, run: go('tools') },
      { id: 'content-engine', label: 'Content Engine', hint: 'Draft on-brand assets', group: 'Navigate', icon: Sparkles, keywords: 'writing copy', run: go('content-engine') },
      { id: 'scoring-engine', label: 'Scoring Engine', hint: 'Lead + health scoring config & analytics', group: 'Navigate', icon: Gauge, keywords: 'aqi score bands lead health', run: go('scoring-engine') },
      { id: 'chat', label: 'Chat', hint: 'Talk to the Argo agent', group: 'Navigate', icon: MessageSquare, run: go('chat') },
    ];
    if (canAdmin) {
      list.push({ id: 'admin', label: 'Admin', hint: 'Users · settings · audit', group: 'Admin', icon: Shield, run: go('admin') });
    }
    return list;
  }, [onNavigate, onSelectContactsTab, onSelectProjectsTab, onClose, canAdmin]);

  const q = query.trim().toLowerCase();

  const filteredCommands = useMemo(
    () => commands.filter((c) => matchesCommand(c, q)),
    [commands, q],
  );

  // Live record matches across people / accounts / tickets / tasks / agents.
  // Ranked by where the query hits (prefix beats mid-string), capped so the
  // list stays tight.
  const entityMatches = useMemo<EntityMatch[]>(() => {
    if (!q) return [];
    const hit = (hay: string | null | undefined) => rankHit(hay, q);
    const out: EntityMatch[] = [];

    for (const p of people) {
      const rank = Math.min(hit(p.name), hit(p.company), hit(p.email), hit(p.title));
      if (rank === Infinity) continue;
      out.push({
        target: { kind: 'person', id: p.id, tab: personTab(p.category) },
        title: p.name,
        sub:
          [p.title, p.company].filter(Boolean).join(' · ') ||
          p.email ||
          `${p.category}`,
        rank,
      });
    }

    for (const a of accounts) {
      const rank = Math.min(hit(a.name), hit(a.pocName), hit(a.ownerName));
      if (rank === Infinity) continue;
      out.push({
        target: { kind: 'account', id: a.id },
        title: a.name,
        sub: a.pocName ? `Account · POC ${a.pocName}` : 'Account',
        rank,
      });
    }

    for (const t of tickets) {
      const rank = Math.min(hit(t.id), hit(t.title));
      if (rank === Infinity) continue;
      out.push({
        target: { kind: 'ticket', id: t.id },
        title: `${t.id} · ${t.title}`,
        sub: `Ticket · ${t.status}`,
        rank,
      });
    }

    for (const t of tasks) {
      const rank = Math.min(
        hit(t.title),
        hit(t.notes),
        hit(t.assignee ?? undefined),
        hit(t.accountName),
        hit(t.personName),
      );
      if (rank === Infinity) continue;
      const ctx = t.accountName || t.personName || t.assignee || '';
      out.push({
        target: { kind: 'task', id: t.id },
        title: t.title,
        sub: ['Task', t.status, ctx].filter(Boolean).join(' · '),
        rank,
      });
    }

    for (const a of agents) {
      const rank = Math.min(hit(a.name), hit(a.description));
      if (rank === Infinity) continue;
      out.push({
        target: { kind: 'agent', id: a.id },
        title: a.name,
        sub: ['Agent', a.state].filter(Boolean).join(' · '),
        rank,
      });
    }

    out.sort((x, y) => x.rank - y.rank || x.title.localeCompare(y.title));
    return out.slice(0, 8);
  }, [q, people, accounts, tickets, tasks, agents]);

  // With a query → live matches; with an empty box → recently-jumped records.
  const shownEntities = useMemo<EntityMatch[]>(() => {
    if (q) return entityMatches;
    return recents.map((r) => ({ target: r.target, title: r.title, sub: r.sub, rank: 0 }));
  }, [q, entityMatches, recents]);
  const entitiesAreRecent = q.length === 0 && shownEntities.length > 0;

  // One ordered, indexable list so keyboard + mouse share a single model:
  // Ask Argo (when text) → matched/recent records → navigation commands.
  const rows = useMemo<Row[]>(() => {
    const list: Row[] = [];
    if (q.length > 0) list.push({ type: 'ask', text: query.trim() });
    for (const m of shownEntities) list.push({ type: 'entity', match: m });
    for (const c of filteredCommands) list.push({ type: 'nav', cmd: c });
    return list;
  }, [q, query, shownEntities, filteredCommands]);

  // Clamp selection whenever the result set changes.
  useEffect(() => {
    setSel((s) => (rows.length === 0 ? 0 : Math.min(s, rows.length - 1)));
  }, [rows.length]);

  if (!open) return null;

  const runRow = (index: number) => {
    const row = rows[index];
    if (!row) return;
    if (row.type === 'ask') {
      const text = row.text.trim();
      if (!text) return;
      onAskArgo(text);
      onClose();
    } else if (row.type === 'entity') {
      const { target, title, sub } = row.match;
      pushRecent({ target, title, sub });
      onJump(target);
      onClose();
    } else {
      row.cmd.run();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => (rows.length === 0 ? 0 : (s + 1) % rows.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => (rows.length === 0 ? 0 : (s - 1 + rows.length) % rows.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runRow(sel);
    }
  };

  // Track section headers as we render the ordered rows.
  let lastNavGroup: string | null = null;
  let entityHeaderShown = false;

  return (
    <div
      className="cmdk-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="cmdk-wrap"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <GlassPanel animate={false} style={{ padding: 0, overflow: 'hidden' }}>
          {/* Search row */}
          <div className="cmdk-search">
            <Search size={16} style={{ color: C.textDim, flexShrink: 0 }} />
            <input
              ref={inputRef}
              className="glass-input"
              placeholder="Search records, jump to a view, or ask Argo…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ background: 'transparent', border: 'none', boxShadow: 'none', flex: 1, fontSize: 15 }}
            />
            <span className="glass-pill" style={{ flexShrink: 0 }}>esc</span>
          </div>

          {/* Results */}
          <div className="cmdk-list">
            {rows.length === 0 && (
              <div className="cmdk-empty t-meta">No matches.</div>
            )}

            {rows.map((row, idx) => {
              const active = sel === idx;

              if (row.type === 'ask') {
                return (
                  <button
                    key="ask"
                    type="button"
                    className={`cmdk-row${active ? ' cmdk-row--active' : ''}`}
                    onMouseEnter={() => setSel(idx)}
                    onClick={() => runRow(idx)}
                  >
                    <span className="cmdk-row__icon" style={{ color: C.magentaSoft }}>
                      <Bot size={15} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="t-card-title" style={{ display: 'block' }}>
                        Ask Argo: “{row.text}”
                      </span>
                      <span className="t-meta">Send to the agent — it’ll propose the writes</span>
                    </span>
                    <CornerDownLeft size={13} style={{ color: C.textDim, flexShrink: 0 }} />
                  </button>
                );
              }

              if (row.type === 'entity') {
                const { match } = row;
                const { icon: Icon, color } = visualForTarget(match.target);
                const showHeader = !entityHeaderShown;
                entityHeaderShown = true;
                return (
                  <div key={`ent-${match.target.kind}-${match.target.id}`}>
                    {showHeader && (
                      <div className="cmdk-group t-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {entitiesAreRecent && <Clock size={10} />}
                        {entitiesAreRecent ? 'Recent' : 'Jump to'}
                      </div>
                    )}
                    <button
                      type="button"
                      className={`cmdk-row${active ? ' cmdk-row--active' : ''}`}
                      onMouseEnter={() => setSel(idx)}
                      onClick={() => runRow(idx)}
                    >
                      <span className="cmdk-row__icon" style={{ color }}>
                        <Icon size={15} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          className="t-card-title"
                          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          <Highlight text={match.title} q={q} />
                        </span>
                        <span className="t-meta">
                          <Highlight text={match.sub} q={q} />
                        </span>
                      </span>
                      <CornerDownLeft size={13} style={{ color: C.textDim, flexShrink: 0 }} />
                    </button>
                  </div>
                );
              }

              // nav
              const c = row.cmd;
              const Icon = c.icon;
              const showGroup = c.group !== lastNavGroup;
              lastNavGroup = c.group;
              return (
                <div key={c.id}>
                  {showGroup && <div className="cmdk-group t-eyebrow">{c.group}</div>}
                  <button
                    type="button"
                    className={`cmdk-row${active ? ' cmdk-row--active' : ''}`}
                    onMouseEnter={() => setSel(idx)}
                    onClick={() => runRow(idx)}
                  >
                    <span className="cmdk-row__icon"><Icon size={15} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="t-card-title" style={{ display: 'block' }}>
                        <Highlight text={c.label} q={q} />
                      </span>
                      <span className="t-meta">{c.hint}</span>
                    </span>
                    <ArrowRight size={13} style={{ color: C.textDim, flexShrink: 0 }} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="cmdk-foot t-meta">
            <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
            <span><kbd>↵</kbd> select</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Users size={11} /> {rows.length} result{rows.length === 1 ? '' : 's'}
            </span>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}
